import { FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useParams } from 'react-router';
import {
  PopConfirm,
  Button,
  Col,
  Row,
  Notification,
  UploadFile,
} from 'fave-ui';
import Form from '../../../../components/common/form/Form';
import { Input } from '../../../../components/common/form/Input';
import SubmitButton from '../../../../components/common/form/SubmitButton';
import { StatusType } from '../../../../types/dataTypes';
import { PageHeaderWithBackButton } from '../../../../components/common/PageHeaderWithBackButton';
import {
  useFetchCashbackCampaignListingData,
  useUpdateWhitelistedMutation,
} from '../../../../services/CashbackCampaign/useFetchCashbackCampaignListing';
import { Select } from '../../../../components/common/form/Select';
import {
  appendFormData,
  arrayToOptions,
} from '../../../../utils/utilFunctions';

type CampaignFormFields = {
  id?: number;
  campaign_type: string;
  whitelisted_company_ids?: (string | number)[];
  csv_file?: UploadFile[];
  csv_option?: string;
  isLoading?: boolean;
  isError?: boolean;
  status?: StatusType;
};

const successNotification = () => {
  Notification.success({
    message: 'Campaign Successfully Edited!',
    description: 'Your cashback campaign has been edited successfully',
  });
};

const EditFaveCashbackCampaignSchema = Yup.object().shape({
  whitelisted_company_ids: Yup.array().required('Please include ids'),
  csv_file: Yup.array().optional(),
  csv_option: Yup.string().when('csv_file', {
    is: (csv_file: {}[]) => csv_file && csv_file.length > 0,
    then: Yup.string().required('Please select an option'),
    otherwise: Yup.string().optional(),
  }),
});

const CSV_OPTIONS = [
  {
    key: 'append',
    label: 'Append to existing list',
    value: 'append',
  },
  {
    key: 'replace',
    label: 'Replace existing list',
    value: 'replace',
  },
  {
    key: 'remove',
    label: 'Remove from existing list',
    value: 'remove',
  },
];

export default function EditFaveCashbackCampaign() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: cashbackCampaignData } = useFetchCashbackCampaignListingData({
    id: id,
  });
  const { mutateAsync: updateCampaignWhitelisted } =
    useUpdateWhitelistedMutation();

  const idOptions =
    cashbackCampaignData?.whitelisted_company_ids &&
    arrayToOptions(
      cashbackCampaignData?.whitelisted_company_ids.map(item =>
        item.toString(),
      ),
      ' ',
    );

  const initialValues: CampaignFormFields = {
    campaign_type: cashbackCampaignData?.campaign_type as string,
    whitelisted_company_ids: cashbackCampaignData?.whitelisted_company_ids,
  };

  const handleSubmit = async (
    values: CampaignFormFields,
    { setSubmitting }: FormikHelpers<CampaignFormFields>,
  ) => {
    const csv =
      values.csv_file && values.csv_file.length > 0
        ? values.csv_file[0].originFileObj
        : null;

    const formData = appendFormData({
      whitelisted_company_ids: csv,
      csv_option: values.csv_option,
    });

    await updateCampaignWhitelisted(
      { id: id as string | number, formData },
      {
        onSuccess: () => {
          successNotification();
        },
        onSettled: () => {
          setSubmitting(false);
        },
      },
    );
  };

  return (
    <>
      <PageHeaderWithBackButton title={`Edit Cashback Campaign`} />
      <Form
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={EditFaveCashbackCampaignSchema}
        validateOnMount
        validateOnChange
        validateOnSchemaChange
      >
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Input
              name="campaign_type"
              label="Campaign Type"
              disabled
              style={{ marginBottom: 0 }}
            />
          </Col>
          <Col span={24}>
            <Select
              name="whitelisted_company_ids"
              label="Whitelisted Merchants"
              placeholder=""
              mode="tags"
              bordered
              options={idOptions}
              style={{ marginBottom: 0 }}
              disabled
            />
          </Col>
          <Col span={24}>
            <Input
              name="csv_file"
              type="file"
              Text="CSV file should have 1 column with IDs and no headers."
              accept=".csv"
              max={1}
            />
          </Col>
          <Col span={24}>
            <Select
              name="csv_option"
              label=""
              placeholder="csv option"
              bordered
              options={CSV_OPTIONS}
            />
          </Col>
          <Col span={24}>
            <SubmitButton disableIfNotValid disableIfNotDirty>
              {(_, isSubmitting) => (isSubmitting ? 'Submitting...' : 'Save')}
            </SubmitButton>
            <PopConfirm
              title="Are you sure you want to cancel? All progress will be lost for the current campaign."
              onConfirm={() =>
                navigate('/admin/merchandise_and_campaign/cashback_campaigns')
              }
              okText="Yes, I am sure"
              cancelText="Go Back"
            >
              <Button type="primary" size="large">
                Cancel
              </Button>
            </PopConfirm>
          </Col>
        </Row>
      </Form>
    </>
  );
}
